// useAuditStream — shared hook for one-shot SSE/text calls to /api/ai/chat.
//
// AuditPage uses this to fire a fixed prompt for a given session type and get
// the full text response back as a single string.
//
// TerminalChatStream uses the full useChat (with typewriter) and doesn't share
// this hook — but the transport is the same: POST /api/ai/chat, read the body
// as a stream, accumulate text. Keeping them separate avoids constraining
// TerminalChatStream's UI to the audit shape.

import { useCallback, useState } from "react";
import { getAccessToken } from "../../lib/supabase";

export interface UseAuditStreamOptions {
  session: "weekly" | "monthly";
}

export interface UseAuditStreamResult {
  text: string;
  isLoading: boolean;
  error: string | null;
  run: () => Promise<void>;
}

export function useAuditStream({ session }: UseAuditStreamOptions): UseAuditStreamResult {
  const [text, setText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setText("");
    try {
      const token = await getAccessToken();
      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          sessionType: session,
          messages: [{ role: "user", content: `Generate my ${session} audit.` }],
        }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      }
      const reader = res.body?.getReader();
      if (!reader) {
        throw new Error("no response body");
      }
      const decoder = new TextDecoder();
      let buf = "";
      // Stream-read loop — continuously accumulate the response and update
      // local state so the UI sees progressively more text.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        setText(buf);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  return { text, isLoading, error, run };
}
